import OpenAI from 'openai';
import { toast } from './toast';
import { supabaseClient } from './supabase';

// Модель для генерації контенту
const DEFAULT_MODEL = 'gpt-4.1';

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface GenerationCost {
  inputTokens: number;
  outputTokens: number;
  inputCost: number;
  outputCost: number;
  totalCost: number;
  model: string;
}

// Ціни для GPT-4.1 (в доларах за 1M токенів)
const MODEL_PRICES = {
  'gpt-4.1': {
    input: 2.50,
    output: 10.00
  },
  'gpt-4.1-mini': {
    input: 0.15,
    output: 0.60
  },
  'gpt-4o': {
    input: 2.50,
    output: 10.00
  },
  'gpt-4o-mini': {
    input: 0.15,
    output: 0.60
  }
};

// Function to get OpenAI API key from database
async function getOpenAIApiKey(): Promise<string> {
  const { data, error } = await supabaseClient
    .from('api_services')
    .select('api_key, is_active')
    .eq('service_type', 'openai')
    .single();

  if (error || !data) {
    toast.error('Failed to fetch OpenAI credentials');
    throw new Error('Failed to fetch OpenAI credentials');
  }
  if (!data.is_active) {
    toast.error('OpenAI service is not active');
    throw new Error('OpenAI service is not active');
  }

  return data.api_key;
}

// Function to create OpenAI client
async function createOpenAIClient(): Promise<OpenAI> {
  const apiKey = await getOpenAIApiKey();
  return new OpenAI({
    apiKey,
    dangerouslyAllowBrowser: true
  });
}

/**
 * Calculate cost for token usage
 */
export function calculateCost(
  inputTokens: number,
  outputTokens: number,
  model: string = DEFAULT_MODEL
): GenerationCost {
  const prices = MODEL_PRICES[model as keyof typeof MODEL_PRICES] || MODEL_PRICES[DEFAULT_MODEL];
  
  const inputCost = (inputTokens / 1_000_000) * prices.input;
  const outputCost = (outputTokens / 1_000_000) * prices.output;
  const totalCost = inputCost + outputCost;

  return {
    inputTokens,
    outputTokens,
    inputCost,
    outputCost,
    totalCost,
    model
  };
}

/**
 * Generate content using Chat Completions API with GPT-4.1
 * Returns both the response and cost information
 */
export async function generateChatCompletion(
  messages: Message[],
  model: string = DEFAULT_MODEL,
  temperature: number = 0.7,
  maxTokens?: number
): Promise<{ content: string; cost: GenerationCost }> {
  try {
    const openai = await createOpenAIClient();

    const response = await openai.chat.completions.create({
      model,
      messages: messages.map(msg => ({
        role: msg.role,
        content: msg.content
      })),
      temperature,
      max_tokens: maxTokens || 16000 // Збільшено з 4000 до 16000 для великих статей
    });

    const content = response.choices[0]?.message?.content || '';
    const usage = response.usage;

    if (!usage) {
      throw new Error('No usage information returned from API');
    }

    const cost = calculateCost(
      usage.prompt_tokens,
      usage.completion_tokens,
      model
    );

    console.log('💰 Generation cost:', {
      model,
      inputTokens: usage.prompt_tokens,
      outputTokens: usage.completion_tokens,
      totalCost: `$${cost.totalCost.toFixed(4)}`
    });

    return { content, cost };

  } catch (error) {
    console.error('Error in generateChatCompletion:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to generate content');
    throw error;
  }
}

/**
 * Wrapper for backward compatibility with existing code
 */
export async function generateContentWithCost(
  prompt: string,
  model: string = 'gpt-4.1'
): Promise<{ content: string; cost: GenerationCost }> {
  return generateChatCompletion([
    {
      role: 'user',
      content: prompt
    }
  ], model);
}
